import React, { useState, useEffect } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Col, Row } from 'olive-react-myoneui';
import Button from 'olive-react-myoneui/lib/Button';
import ls from 'localstorage-slim';
import { Chart, ArcElement, Tooltip, Legend } from 'chart.js';
import { pieIcon } from '../../../constants/icons';

const CircleRadiusChart = ({ data }) => {
  const [isChartVisible, setIsChartVisible] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const dataName = data.map((items) => items.name);
  const dataCountValues = data.map((items) => items.value);
  const computedLabels = dataName.slice(0, 3);
  const dataValues = dataCountValues.slice(0, 3);
  const total = dataCountValues.reduce((acc, value) => acc + value, 0);
  const backgroundColors = ['#539A86', '#2F64BD', '#ADBEFF'];
  const [legendStates, setLegendStates] = useState(computedLabels.map(() => false));
  const roles = JSON.parse(ls.get('roles'));
  const learnerName = roles?.filter((role) => role.identifier === 'apprentice')[0]?.name;

  Chart.register(ArcElement, Tooltip, Legend);

  const [chartData, setChartData] = useState({
    labels: computedLabels,
    datasets: [
      {
        data: dataValues,
        backgroundColor: backgroundColors,
        borderWidth: 1,
      },
    ],
  });

  const chartOptions = {
    aspectRatio: 1,
    cutout: '55%',
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: '#263238',
        bodyAlign: 'bottom',
        padding: 5,
        caretSize: 0
      }
    },
  };

  // Custom plugin to render percentage labels
  const percentageLabelPlugin = {
    id: 'percentageLabel',
    afterDraw: (chart) => {
      const ctx = chart.ctx;
      const datasets = chart.data.datasets;

      datasets.forEach((dataset, datasetIndex) => {
        const total = dataset.data.reduce((acc, value) => acc + value, 0);

        dataset.data.forEach((value, index) => {
          const percentage = ((value / total) * 100).toFixed(1) + '%';
          const meta = chart.getDatasetMeta(datasetIndex);
          const arc = meta.data[index];

          if (arc && dataset.data[index] > 0) {
            const position = arc.tooltipPosition();

            ctx.fillStyle = '#FFFFFF'; // Label color
            ctx.font = 'bold 10px Arial';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(percentage, position.x, position.y);
          }
        });
      });
    },
  };

  Chart.register(percentageLabelPlugin);

  const handleLegendItemClick = (index) => {
    const newLegendStates = [...legendStates];
    newLegendStates[index] = !newLegendStates[index];
    setLegendStates(newLegendStates);

    const newBackgroundColors = [...chartData.datasets[0].backgroundColor];

    if (newLegendStates[index]) {
      chartData.datasets[0].data[index] = 0;
    } else {
      chartData.datasets[0].data[index] = dataValues[index];
    }

    setChartData((prevChartData) => ({
      ...prevChartData,
      datasets: [
        {
          ...prevChartData.datasets[0],
          backgroundColor: newBackgroundColors,
        },
      ],
    }));
  };

  const handleResize = () => {
    setWindowWidth(window.innerWidth);
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const toggleChartVisibility = () => {
    setIsChartVisible((prev) => !prev);
  };

  return (
    <Row className="chart-wrapper program-chart-wrapper">
      <Col sm={12}>
        <div className="chart-label-wrapper">
          {(windowWidth <= 1368 || windowWidth >= 1681 || isChartVisible) ? (
            <div className="chart-container">
              {dataValues.every(value => value === 0) ? (
                <div className="chart-not-available">
                  <span>No Chart Available</span>
                </div>
              ): (
              <Doughnut options={chartOptions} data={chartData} />
              )}
            </div>
          ) : null}
          <div className="legend-container">
            {computedLabels.map((label, index) => (
              <div
                key={index}
                className={`legend-item ${legendStates[index] ? 'selected' : ''}`}
                onClick={() => handleLegendItemClick(index)}
              >
                <div className="legend-details">
                  <div
                    className="legend-color"
                    style={{ backgroundColor: backgroundColors[index] }}
                  ></div>
                  <div
                    className="legend-label"
                    title={label}
                  >
                    {label} ({dataValues[index]}/{total})
                  </div>
                </div>
              </div>
            ))}
            {windowWidth >= 1368 && windowWidth < 1681 && (
              <Button variant="outline" onClick={toggleChartVisibility} className="btn btn-sm btn-round btn-view-piechart">{pieIcon} {!isChartVisible ? 'View' : 'Hide'} chart</Button>
            )}
          </div>
        </div>
      </Col>
    </Row>
  );
};

export default CircleRadiusChart;
